import type {
  AdminCatalogMediaListInput,
  AdminCatalogMediaSummary,
  AttachCatalogMediaInput,
  CatalogMediaOwnerMutationResult,
  CatalogProduct,
  DetachCatalogMediaInput,
  InitiateCatalogMediaUploadInput,
  ProductCategory,
} from '@emazy/shared-types';

import type { AdminCatalogApi } from '../../api';
import type { CatalogMediaUploadTransport } from '../services';
import type { AdminCatalogMediaApi } from './types';

let sequence = 0;

export class MockAdminCatalogMediaApi implements AdminCatalogMediaApi {
  readonly records: AdminCatalogMediaSummary[] = [];
  constructor(private readonly catalog: AdminCatalogApi) {}

  async initiate(input: InitiateCatalogMediaUploadInput) {
    const id = `00000000-0000-4000-8000-${String(++sequence).padStart(12, '0')}`;
    this.records.unshift({
      id,
      status: 'initiated',
      originalFileName: input.fileName,
      declaredContentType: input.contentType,
      variants: [],
      ownerType: 'unassigned',
      createdAt: new Date().toISOString(),
      processingVersion: 1,
      version: 1,
    });
    return {
      mediaId: id,
      uploadUrl: 'mock://catalog-media-upload',
      method: 'POST' as const,
      fields: {},
      expiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
      maxSizeBytes: 20 * 1024 * 1024,
    };
  }
  async list(input: AdminCatalogMediaListInput) {
    const filtered = this.records.filter(
      (item) =>
        (!input.exactMediaId || item.id === input.exactMediaId) &&
        (!input.status || item.status === input.status) &&
        (!input.ownerType || item.ownerType === input.ownerType) &&
        (!input.ownerId || item.ownerId === input.ownerId),
    );
    return { items: filtered.slice(0, input.limit ?? 24), nextCursor: null };
  }
  async get(id: string) {
    const record = this.required(id);
    return { ...record, variants: [...record.variants] };
  }
  async retry(id: string) {
    const record = this.required(id);
    record.status = 'processing';
    record.failureCode = undefined;
    record.version += 1;
    return record;
  }
  async delete(id: string) {
    const record = this.required(id);
    if (record.ownerType !== 'unassigned') throw new Error('Attached media cannot be deleted.');
    record.status = 'deleted';
    record.version += 1;
    return record;
  }
  async attach(id: string, input: AttachCatalogMediaInput) {
    const record = this.required(id);
    if (record.status !== 'ready') throw new Error('Media is not ready.');
    const reference = {
      id,
      kind: 'image' as const,
      role: input.role,
      objectKey: record.variants.find((item) => item.name === 'original-display')!.objectKey,
      altText: input.altText,
      displayOrder: input.displayOrder,
    };
    let result: CatalogMediaOwnerMutationResult;
    if (input.ownerType === 'product') {
      const owner = await this.catalog.getProduct(input.ownerId);
      const updated = await this.catalog.updateProduct(
        owner.id,
        productUpdate(owner, reference, input.expectedOwnerVersion),
      );
      result = { media: record, product: updated };
    } else {
      const owner = await this.catalog.getCategory(input.ownerId);
      const updated = await this.catalog.updateCategory(
        owner.id,
        categoryUpdate(owner, reference, input.expectedOwnerVersion),
      );
      result = { media: record, category: updated };
    }
    record.ownerType = input.ownerType;
    record.ownerId = input.ownerId;
    record.altText = input.altText;
    record.version += 1;
    return result;
  }
  async detach(id: string, input: DetachCatalogMediaInput) {
    const record = this.required(id);
    let result: CatalogMediaOwnerMutationResult = { media: record };
    if (record.ownerType === 'product' && record.ownerId) {
      const owner = await this.catalog.getProduct(record.ownerId);
      const updated = await this.catalog.updateProduct(
        owner.id,
        productUpdate(owner, undefined, input.expectedOwnerVersion, id),
      );
      result = { media: record, product: updated };
    } else if (record.ownerType === 'category' && record.ownerId) {
      const owner = await this.catalog.getCategory(record.ownerId);
      const updated = await this.catalog.updateCategory(
        owner.id,
        categoryUpdate(owner, undefined, input.expectedOwnerVersion),
      );
      result = { media: record, category: updated };
    }
    record.ownerType = 'unassigned';
    record.ownerId = undefined;
    record.altText = undefined;
    record.version += 1;
    return result;
  }
  complete(id: string, file: File) {
    const record = this.required(id);
    record.status = 'ready';
    record.detectedContentType = record.declaredContentType;
    record.originalSizeBytes = file.size;
    record.originalWidth = 1200;
    record.originalHeight = 1200;
    record.processedAt = new Date().toISOString();
    record.variants = ['thumbnail', 'card', 'detail', 'zoom', 'original-display'].map((name) => ({
      name: name as AdminCatalogMediaSummary['variants'][number]['name'],
      objectKey: `catalog/${id}/v1/${name}.webp`,
      width: name === 'thumbnail' ? 240 : 1200,
      height: name === 'thumbnail' ? 240 : 1200,
      contentType: 'image/webp',
      sizeBytes: Math.max(1, Math.round(file.size / 2)),
    }));
    record.version += 2;
  }
  private required(id: string) {
    const record = this.records.find((item) => item.id === id);
    if (!record) throw new Error('Media was not found.');
    return record;
  }
}

export class MockCatalogMediaUploadTransport implements CatalogMediaUploadTransport {
  constructor(private readonly api: MockAdminCatalogMediaApi) {}
  async upload(input: Parameters<CatalogMediaUploadTransport['upload']>[0]) {
    if (input.signal?.aborted) throw new DOMException('Upload canceled.', 'AbortError');
    input.onProgress?.({
      loadedBytes: input.file.size,
      totalBytes: input.file.size,
      percentage: 100,
    });
    this.api.complete(input.initiation.mediaId, input.file);
  }
}

function productUpdate(
  owner: CatalogProduct,
  add: CatalogProduct['media'][number] | undefined,
  expectedVersion: number,
  removeId?: string,
) {
  const {
    id: _id,
    status: _status,
    version: _version,
    createdAt: _created,
    updatedAt: _updated,
    publishedAt: _published,
    archivedAt: _archived,
    ...fields
  } = owner;
  void [_id, _status, _version, _created, _updated, _published, _archived];
  return {
    ...fields,
    slug: owner.slug,
    expectedVersion,
    media: [
      ...owner.media.filter(
        (item) => item.id !== removeId && !(add?.role === 'primary' && item.role === 'primary'),
      ),
      ...(add ? [add] : []),
    ],
  };
}
function categoryUpdate(
  owner: ProductCategory,
  heroMedia: ProductCategory['heroMedia'],
  expectedVersion: number,
) {
  const {
    id: _id,
    status: _status,
    version: _version,
    createdAt: _created,
    updatedAt: _updated,
    publishedAt: _published,
    archivedAt: _archived,
    ...fields
  } = owner;
  void [_id, _status, _version, _created, _updated, _published, _archived];
  const { heroMedia: _oldHero, ...withoutHero } = fields;
  void _oldHero;
  return { ...withoutHero, ...(heroMedia ? { heroMedia } : {}), slug: owner.slug, expectedVersion };
}
